import { Flex, HStack, Tag, Text, VStack } from '@chakra-ui/react';
import { Workshop } from 'api/types';
import { get } from 'lodash';
import ReactCountryFlag from 'react-country-flag';
import { useTranslation } from 'react-i18next';

import Link from '../../components/Link';
import { StatusTag } from '../../components/StatusTag';
import { FlagCountryFromLanguage } from '../../constants';
import { dateTimeTransformer } from '../../helpers/dateTransformer';
import { displayRow } from '../../helpers/general';
import useSystemPartnerQuery from '../../hooks/useSystemPartnerQuery';
import { ContentCard } from '../../layout/ContentCard';

const WorkshopCard = (props: { isLoading: boolean; statusCodes: any; workshop: Workshop | undefined }) => {
  const { t } = useTranslation();
  const { isLoading, workshop } = props;

  const systemPartnerQuery = useSystemPartnerQuery();
  const systemPartnerName = systemPartnerQuery.data?.find((s) => s.id === workshop?.systempartner_id);

  return (
    <ContentCard
      isLoading={isLoading}
      //@ts-ignore
      contentDescription={
        <VStack>
          <Link to={`/app/op/workshops/${workshop?.id}`}>{get(workshop, 'name', '')}</Link>

          <Flex alignItems={'center'} justifyContent={'space-between'}>
            {workshop?.status != null && <StatusTag currentStatus={workshop.status} resourceKey={'workshop'} />}
            <Tag
              ml={2}
              size={'sm'}
              variant="solid"
              textTransform={'uppercase'}
              flexShrink={0}
              mt={{ base: 0, lg: 1 }}
              paddingBlock={2}
              textAlign={'center'}
              display={'block'}
              whiteSpace={'normal'}
              colorScheme={workshop?.is_verified ? 'green' : workshop?.is_verification_pending ? 'orange' : 'red'}
            >
              {workshop?.is_verified
                ? t('workshop_verification_request:verified')
                : workshop?.is_verification_pending
                  ? t('workshop_verification_request:verified_pending')
                  : t('workshop_verification_request:not_verified')}
            </Tag>
            {systemPartnerName?.name && (
              <Tag
                ml={2}
                size={'sm'}
                variant="solid"
                textTransform={'uppercase'}
                flexShrink={0}
                mt={{ base: 0, lg: 1 }}
                paddingBlock={2}
                textAlign={'center'}
                display={'block'}
                whiteSpace={'normal'}
                colorScheme={'purple'}
              >
                {systemPartnerName?.name}
              </Tag>
            )}
          </Flex>
          <Text>{workshop?.user_readable_id}</Text>
        </VStack>
      }
      data-test-id="workshop-card"
    >
      {displayRow({
        label: t('workshop:firstname.label'),
        content: get(workshop, 'firstname'),
        isLoading,
      })}
      {displayRow({
        label: t('workshop:lastname.label'),
        content: get(workshop, 'lastname'),
        isLoading,
      })}
      {displayRow({
        label: t('workshop:email.label'),
        content: get(workshop, 'email'),
        isLoading,
      })}
      {displayRow({
        label: t('workshop:telephone.label'),
        content: get(workshop, 'telephone'),
        isLoading,
      })}
      {displayRow({
        label: t('workshop:name.label'),
        content: get(workshop, 'name'),
        isLoading,
      })}
      <HStack display="flex" flexDir="column" alignItems="flex-start">
        {displayRow({
          label: t('workshop:vat_number.label'),
          content: get(workshop, 'vat_number'),
          isLoading,
        })}
        {(workshop?.vat_validation_information?.name || workshop?.vat_validation_information?.address) && (
          <Tag size={'sm'} variant="solid" bgColor={workshop?.vat_validation_information?.valid ? 'green' : 'orange.200'}>
            <Text as={'div'}>{workshop?.vat_validation_information?.name}</Text>,
            <Text as={'div'}>{workshop?.vat_validation_information?.address}</Text>
          </Tag>
        )}
      </HStack>

      {displayRow({
        label: t('workshop:legal_form.label'),
        content: get(workshop, 'legal_form'),
        isLoading,
      })}
      {displayRow({
        label: t('workshop:language.label'),
        content: (
          <>
            <ReactCountryFlag
              //@ts-ignore
              countryCode={FlagCountryFromLanguage[workshop?.language?.toUpperCase()]}
              svg
              style={{
                width: '2em',
                height: '2em',
              }}
            />{' '}
            <Text as="mark" display="inline">
              {workshop?.language ? t(`location:languages.${workshop?.language.toUpperCase()}`) : '-'}
            </Text>{' '}
          </>
        ),
        isLoading,
      })}
      {displayRow({
        label: t('workshop:country.label'),
        content: workshop?.country ? t(`location:countries.${workshop?.country.toUpperCase()}`) : '-',
        isLoading,
      })}
      {displayRow({
        label: t('workshop:city.label'),
        content: get(workshop, 'city'),
        isLoading,
      })}

      {displayRow({
        label: t('workshop:zipcode.label'),
        content: get(workshop, 'zipcode', '-'),
        isLoading,
      })}

      {displayRow({
        label: t('workshop:address.label'),
        content: get(workshop, 'address', '-'),
        isLoading,
      })}
      {displayRow({
        label: t('workshop:registered_at.label'),
        content: dateTimeTransformer(get(workshop, 'registered_at')!),
        isLoading,
      })}
      {displayRow({
        label: t('workshop:activated_at.label'),
        content: dateTimeTransformer(get(workshop, 'activated_at')!),
        isLoading,
      })}
    </ContentCard>
  );
};

export default WorkshopCard;
// function useSystemPartnerQuery() {
//   throw new Error('Function not implemented.');
// }
